import useApi from '../../hooks/api/useApi';
import useMutate from '../../hooks/api/useMutate';

export function useGetServicePeriods(filters) {
  let query = [];

  if (filters?.location_id) query.push(`location_id=${filters.location_id}`);
  if (filters?.date) query.push(`date=${filters.date}`);
  if (filters?.date_from) query.push(`date_from=${filters.date_from}`);
  if (filters?.date_to) query.push(`date_to=${filters.date_to}`);
  if (filters?.service_period) query.push(`service_period=${filters.service_period}`);
  if (filters?.page) query.push(`page=${filters.page}`);
  if (filters?.per_page) query.push(`per_page=${filters.per_page}`);

  const queryString = query.length > 0 ? `?${query.join('&')}` : '';

  const url = `/custom/service-periods${queryString}`;

  const { data, error, isLoading, refresh } = useApi(url, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false
  });

  // Transform response (flatten)
  const transform = (raw = {}) => {
    const flat = [];

    Object.entries(raw).forEach(([location, items]) => {
      items.forEach((item) => {
        flat.push({
          ...item,
          location
        });
      });
    });

    return flat;
  };

  return {
    list: transform(data?.data || {}),
    pagination: data?.pagination || {},
    success: data?.success ?? false,
    error,
    isLoading,
    refresh
  };
}

export function useAddServicePeriod() {
  const { post, isMutating } = useMutate('/custom/service-periods-add');

  const addServicePeriod = async (payload) => await post(payload);

  return { addServicePeriod, isMutating };
}

export function useAddServicePeriodName() {
  const { post, isMutating } = useMutate('/service-periods/default');

  const addServicePeriodName = async (payload) => await post(payload);

  return { addServicePeriodName, isMutating };
}

export function useGetServicePeriodsByLocation(locationId) {
  const url = locationId ? `/service-periods/location/${locationId}` : null;

  const { data, error, isLoading, refresh } = useApi(url, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false
  });

  return {
    data: data?.data || [],
    success: data?.success ?? false,
    error,
    isLoading,
    refresh
  };
}

// export function useViewServicePeriod(id, recordType, recurrenceType) {
//   let url = null;

//   if (id) {
//     url = `/service-periods/view/${id}`;

//     if (recordType) {
//       url += `?record_type=${recordType}`;
//     }
//     if(recurrenceType){

//     }
//   }

//   const { data, error, isLoading, refresh } = useApi(url, {
//     revalidateOnFocus: false,
//     revalidateOnReconnect: false,
//     revalidateIfStale: false
//   });

//   return {
//     servicePeriod: data?.data || null,
//     success: data?.success ?? false,
//     error,
//     isLoading,
//     refresh
//   };
// }

export function useViewServicePeriod(id, recordType, recurrenceType) {
  let url = null;

  if (id) {
    const params = new URLSearchParams();

    if (recordType) {
      params.append('record_type', recordType);
    }

    // if (recurrenceType) {
    //   params.append('recurrence', recurrenceType);
    // }

    if (typeof recurrenceType === 'string') {
      params.append('recurrence', recurrenceType.toLowerCase());
    }

    const query = params.toString();
    url = `/service-periods/view/${id}${query ? `?${query}` : ''}`;
  }

  const { data, error, isLoading, refresh } = useApi(url, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false
  });

  return {
    servicePeriod: data?.data || null,
    success: data?.success ?? false,
    error,
    isLoading,
    refresh
  };
}

export function useUpdateServicePeriod() {
  const updateServicePeriod = async (id, payload) => {
    const { put } = useMutate(`/service-periods/update/${id}`);
    return await put(payload);
  };

  return { updateServicePeriod };
}

export function useDeleteServicePeriod() {
  const deleteServicePeriod = async (id) => {
    if (!id) throw new Error('ID is required');

    const { delete: del } = useMutate(`/service-periods/delete/${id}`);

    return await del();
  };

  return { deleteServicePeriod };
}

export function useServicePeriodDropdown() {
  const url = `/service-periods/names`;

  const { data, error, isLoading, refresh } = useApi(url, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false
  });

  return {
    names: data?.data || [],
    success: data?.success ?? false,
    error,
    isLoading,
    refresh
  };
}

export function useToggleServicePeriodStatus(id, recordType = 'default') {
  const url = id ? `/service-periods/status/${id}?record_type=${recordType}` : null;

  const { data, error, isLoading, refresh } = useApi(url, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    revalidateOnMount: false // IMPORTANT → do not auto call
  });

  return {
    data: data?.data || null,
    success: data?.success ?? false,
    message: data?.message,
    error,
    isLoading,
    trigger: refresh // manual trigger
  };
}
